<template>
  <v-main>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <div class="jsonview-root">
        <Header :text="$tc('sac.jsonTitle', 2)" />

        <section class="jsonview-section">
          <!-- JSON Textarea and Viewer -->
          <main class="jsonview-main">
            <v-textarea
              v-model="jsonInput"
              label="CAE JSON"
              outlined
              auto-grow
              rows="10"
              resize="none"
              class="json-textarea"
              @input="updateJson"
              hide-details
            ></v-textarea>
            <iframe
              id="jsoncrackEmbed"
              src="https://jsoncrack.com/widget"
              width="100%"
              height="500px"
              class="json-iframe"
              @load="sendToEmbed"
            ></iframe>
            <div class="jsonview-actions">
              <v-btn color="grey" dark @click="$router.go(-1)" class="jsonview-btn">
                <v-icon left>mdi-arrow-left</v-icon> Back
              </v-btn>
              <v-btn
                color="primary"
                dark
                @click="handleCreateClick(jsonInput)"
                class="jsonview-btn"
              >
                <v-icon left>mdi-plus</v-icon> Create CAE
              </v-btn>
              <v-btn color="red" dark @click="jsonInput = ''" class="jsonview-btn">
                <v-icon left>mdi-close</v-icon> Clear
              </v-btn>
            </div>
          </main>
        </section>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import Header from '@/components/helpers/Header';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import securityAssuranceCaseApi from '../api/securityAssuranceCaseApi';

export default {
  name: 'AiAssistance',
  components: {
    Header,
    Breadcrumbs,
  },
  data() {
    return {
      jsonInput: '',
      selectedRequirementId: null,
    };
  },
  methods: {
    sendToEmbed() {
      const jsonCrackEmbed = document.getElementById('jsoncrackEmbed');
      if (!jsonCrackEmbed || !jsonCrackEmbed.contentWindow) {
        setTimeout(this.sendToEmbed, 500);
        return;
      }
      const options = {
        theme: 'light',
        direction: 'RIGHT',
      };
      jsonCrackEmbed.contentWindow.postMessage({ json: this.jsonInput, options }, '*');
    },
    updateJson() {
      this.sendToEmbed();
    },
    async fetchRequirementIds() {
      try {
        const { data } = await securityAssuranceCaseApi.getRequirementIds();
        this.requirementIds = Array.isArray(data) ? data : [];
        this.selectedRequirementId = this.requirementIds.find(
          reqId => reqId === this.$route.query.requirement.requirementId,
        );
      } catch (e) {
        this.requirementIds = [];
      }
    },
    async getCaeTemplate() {
      try {
        const { data: response } = await securityAssuranceCaseApi.getCaeTemplate(
          this.selectedRequirementId,
        );

        // The response should already be in Claims format from the backend
        // If it's an array, wrap it in the Claims structure
        let formattedData;
        if (Array.isArray(response)) {
          formattedData = { Claims: response };
        } else if (response.Claims) {
          formattedData = response;
        } else {
          // Convert old format to new format if needed
          formattedData = { Claims: [response] };
        }

        const jsonString = JSON.stringify(formattedData, null, 2);
        this.jsonInput = jsonString;
        this.updateJson();
      } catch (e) {
        if (!this.selectedRequirementId)
          this.jsonInput = '// No Template was found for this Requirement ID';
        else this.jsonInput = '// Failed to fetch CAE template for this requirement.';
      }
    },
    async handleCreateClick(jsonInput) {
      const id = this.$route.query.requirement.id || null;
      try {
        const response = await securityAssuranceCaseApi.composeSac(id, JSON.parse(jsonInput));
        if (response && response.status === 201) {
          localStorage.setItem(
            'socVersionId',
            JSON.stringify(String(this.$route.query.requirement.socVersionId)),
          );
          this.$router.push({ name: 'SacBuilder' });
        }
      } catch (error) {
        console.error('Error composing SAC:', error);
      }
    },
  },
  async mounted() {
    await this.fetchRequirementIds();
    await this.getCaeTemplate();
    this.sendToEmbed();
  },
};
</script>

<style scoped>
.jsonview-root {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.jsonview-section {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  flex-wrap: wrap;
}
.requirement-list {
  min-width: 220px;
  max-width: 260px;
  background: #f8fafc;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(60, 60, 60, 0.06);
  padding: 0.5rem 0.5rem 0.5rem 0.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.requirement-list-header {
  font-weight: 700;
  font-size: 1.1rem;
  color: #1f2937;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem 0.25rem 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0.5rem;
}
.requirement-list-scroll {
  overflow-y: auto;
  max-height: 340px;
  min-height: 180px;
  padding-right: 2px;
}
.requirement-list-item {
  border-radius: 6px;
  margin-bottom: 2px;
  transition: background 0.15s;
}
.requirement-list-item:hover {
  background: #e3e8f7 !important;
}
.selected-requirement {
  background-color: #c7d7f7 !important;
  font-weight: 600;
  color: #1a237e;
}
.jsonview-main {
  flex: 1 1 400px;
  min-width: 0;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  align-items: center; /* Center children horizontally */
  margin: 0 auto; /* Center the main container itself */
}
.json-textarea {
  resize: none;
  min-height: 220px !important;
  max-height: 400px !important;
  overflow-y: scroll !important;
  background: #f4f6fa !important;
  border-radius: 8px !important;
  font-family: 'Fira Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 0.98rem;
  box-shadow: 0 1px 4px rgba(60, 60, 60, 0.04);
  border: 1px solid #dbeafe !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  padding-left: 12px !important;
  padding-right: 12px !important;
  box-sizing: border-box;
  width: 100%;
  max-width: 900px; /* Limit width for centering */
  margin: 0 auto; /* Center horizontally */
}
.json-iframe {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(60, 60, 60, 0.04);
  height: 500px !important;
  width: 100%;
  max-width: 900px; /* Limit width for centering */
  margin: 0 auto; /* Center horizontally */
}
.jsonview-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
  margin-top: 0.2rem;
  width: 100%;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}
.jsonview-btn {
  min-width: 110px;
  font-weight: 600;
  letter-spacing: 0.01em;
  border-radius: 6px;
  box-shadow: none;
  transition: background 0.15s;
}
.jsonview-btn:hover {
  filter: brightness(0.95);
}
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}
.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}
@media (max-width: 900px) {
  .jsonview-section {
    flex-direction: column;
    gap: 1.5rem;
  }
  .requirement-list {
    max-width: 100%;
    min-width: 0;
  }
  .jsonview-main {
    min-width: 0;
    max-width: 100%;
    align-items: center; /* Keep children centered on mobile */
  }
  .json-iframe {
    height: 350px !important;
    max-width: 100%;
  }
  .json-textarea {
    min-height: 140px !important;
    max-height: 220px !important;
    padding-top: 28px !important; /* Adjust for mobile */
    max-width: 100%;
  }
  .jsonview-actions {
    max-width: 100%;
  }
}
</style>
